import React, { useState } from 'react';
import { PlayIcon, RefreshCwIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, InfoIcon, ChevronDownIcon, ChevronRightIcon, DownloadIcon, ClockIcon, ZapIcon, SearchIcon } from 'lucide-react';
import { Button } from '../../ui/Button';
import { runPipeline } from '../../../governance/pipeline/orchestrator';
import { ALL_QUESTIONS_CORE } from '../../../data/questionLoader';
type Severity = 'BLOCKER' | 'WARNING' | 'INFO';
type Stage = 'schema' | 'type' | 'content' | 'support' | 'visual' | 'graph';
type RunStatus = 'idle' | 'running' | 'complete';
interface ValidationIssue {
  id: string;
  questionId: string;
  stage: Stage;
  severity: Severity;
  ruleId: string;
  message: string;
  field?: string;
  suggestion?: string;
}
interface ValidationResult {
  questionId: string;
  status: 'pass' | 'blocked' | 'warning';
  issues: ValidationIssue[];
  score: number;
}
// ─── Domain → Stage mapping ───────────────────────────────────────────────────
const DOMAIN_TO_STAGE: Record<string, Stage> = {
  schema: 'schema',
  type: 'type',
  graph: 'graph',
  content: 'content',
  visual: 'visual',
  support: 'support',
  scoring: 'content',
  gate: 'schema'
};
// ─── Pipeline result mapper ───────────────────────────────────────────────────
function mapPipelineResult(pipelineResult: ReturnType<typeof runPipeline>): ValidationResult {
  const {
    gate,
    questionId
  } = pipelineResult;
  const status: ValidationResult['status'] = gate.verdict === 'PASS' ? 'pass' : gate.verdict === 'REVIEW_NEEDED' ? 'warning' : 'blocked';
  const issues: ValidationIssue[] = gate.issues.map((issue, idx) => ({
    id: `${questionId}-${idx}`,
    questionId,
    stage: DOMAIN_TO_STAGE[issue.domain] ?? 'content',
    severity: issue.kind === 'blocker' ? 'BLOCKER' : issue.kind === 'coach' ? 'WARNING' : 'INFO',
    ruleId: issue.code,
    message: issue.message,
    field: issue.field,
    suggestion: issue.suggestion
  }));
  return {
    questionId,
    status,
    issues,
    score: gate.score
  };
}
const STAGE_LABELS: Record<Stage, string> = {
  schema: 'Schema',
  type: 'Vraagtype',
  content: 'Inhoud',
  support: 'Ondersteuning',
  visual: 'Visueel',
  graph: 'LDG-Graaf'
};
const SEVERITY_CONFIG: Record<Severity, {
  icon: React.ReactNode;
  bg: string;
  text: string;
  border: string;
  label: string;
}> = {
  BLOCKER: {
    icon: <XCircleIcon className="h-4 w-4" data-id="element-3473" />,
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    label: 'Blokkerend'
  },
  WARNING: {
    icon: <AlertTriangleIcon className="h-4 w-4" data-id="element-3474" />,
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    label: 'Waarschuwing'
  },
  INFO: {
    icon: <InfoIcon className="h-4 w-4" data-id="element-3475" />,
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    label: 'Informatief'
  }
};
function IssueRow({
  issue
}: {
  issue: ValidationIssue;
}) {
  const sev = SEVERITY_CONFIG[issue.severity];
  return <div className={`rounded-lg border ${sev.border} ${sev.bg} p-3`} data-id="element-3476">
      <div className="flex items-start gap-2" data-id="element-3477">
        <span className={`flex-shrink-0 mt-0.5 ${sev.text}`} data-id="element-3478">{sev.icon}</span>
        <div className="min-w-0 flex-1" data-id="element-3479">
          <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-3480">
            <code className="text-xs text-gray-400 font-mono" data-id="element-3481">
              {issue.ruleId}
            </code>
            <span className={`text-xs font-semibold ${sev.text}`} data-id="element-3482">
              {sev.label}
            </span>
            <span className="text-xs text-gray-500 bg-white/70 rounded px-1.5 py-0.5 border border-gray-200" data-id="element-3483">
              {STAGE_LABELS[issue.stage]}
            </span>
            {issue.field && <code className="text-xs text-gray-400 bg-white/70 rounded px-1.5 py-0.5 border border-gray-200" data-id="element-3484">
                .{issue.field}
              </code>}
          </div>
          <p className="text-sm text-gray-700" data-id="element-3485">{issue.message}</p>
          {issue.suggestion && <p className="text-xs text-gray-500 mt-1" data-id="element-3486">
              <span className="font-medium" data-id="element-3487">Oplossing:</span> {issue.suggestion}
            </p>}
        </div>
      </div>
    </div>;
}
function ResultRow({
  result
}: {
  result: ValidationResult;
}) {
  const [expanded, setExpanded] = useState(false);
  const statusConfig = {
    pass: {
      icon: <CheckCircleIcon className="h-4 w-4 text-emerald-500" data-id="element-3488" />,
      text: 'text-emerald-700',
      bg: 'bg-emerald-50',
      label: 'Geslaagd'
    },
    blocked: {
      icon: <XCircleIcon className="h-4 w-4 text-red-500" data-id="element-3489" />,
      text: 'text-red-700',
      bg: 'bg-red-50',
      label: 'Geblokkeerd'
    },
    warning: {
      icon: <AlertTriangleIcon className="h-4 w-4 text-amber-500" data-id="element-3490" />,
      text: 'text-amber-700',
      bg: 'bg-amber-50',
      label: 'Waarschuwing'
    }
  };
  const cfg = statusConfig[result.status];
  const scoreColor = result.score >= 90 ? 'text-emerald-600' : result.score >= 70 ? 'text-amber-600' : 'text-red-600';
  return <div className="rounded-lg border border-gray-100 bg-white overflow-hidden" data-id="element-3491">
      <button className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50/50 transition-colors" onClick={() => result.issues.length > 0 && setExpanded(!expanded)} disabled={result.issues.length === 0} data-id="element-3492">
        <span className="flex-shrink-0" data-id="element-3493">{cfg.icon}</span>
        <div className="min-w-0 flex-1" data-id="element-3494">
          <code className="text-sm font-mono text-gray-700" data-id="element-3495">
            {result.questionId}
          </code>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-3496">
          <span className={`text-sm font-bold ${scoreColor}`} data-id="element-3497">
            {result.score}%
          </span>
          {result.issues.length > 0 && <span className="text-xs text-gray-500" data-id="element-3498">
              {result.issues.length} issue{result.issues.length > 1 ? 's' : ''}
            </span>}
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cfg.bg} ${cfg.text}`} data-id="element-3499">
            {cfg.label}
          </span>
          {result.issues.length > 0 && <span className="text-gray-400" data-id="element-3500">
              {expanded ? <ChevronDownIcon className="h-4 w-4" data-id="element-3501" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-3502" />}
            </span>}
        </div>
      </button>
      {expanded && result.issues.length > 0 && <div className="border-t border-gray-100 px-4 py-3 space-y-2 bg-gray-50/50" data-id="element-3503">
          {result.issues.map(issue => <IssueRow key={issue.id} issue={issue} data-id="element-3504" />)}
        </div>}
    </div>;
}
export function GovernanceValidationTab() {
  const [runStatus, setRunStatus] = useState<RunStatus>('idle');
  const [results, setResults] = useState<ValidationResult[]>([]);
  const [progress, setProgress] = useState(0);
  const [filter, setFilter] = useState<'all' | 'blocked' | 'warning' | 'pass'>('all');
  const [search, setSearch] = useState('');
  const [selectedScope, setSelectedScope] = useState<string>('all');
  const [runDuration, setRunDuration] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState<string>('');
  const handleRun = () => {
    setRunStatus('running');
    setProgress(0);
    setResults([]);
    setCurrentStage('Schema laden...');
    const startTime = Date.now();
    // Filter questions by scope
    const questions = selectedScope === 'all' ? ALL_QUESTIONS_CORE : ALL_QUESTIONS_CORE.filter(q => q.id.startsWith(selectedScope));
    const BATCH_SIZE = 20;
    let processed = 0;
    const allResults: ValidationResult[] = [];
    const stages = ['Schema...', 'Type...', 'Inhoud...', 'Visueel...', 'Ondersteuning...', 'LDG-Graaf...'];
    const processBatch = () => {
      const batch = questions.slice(processed, processed + BATCH_SIZE);
      for (const question of batch) {
        try {
          const pipelineResult = runPipeline(question);
          allResults.push(mapPipelineResult(pipelineResult));
        } catch {
          // Skip questions that cause pipeline errors
        }
        processed++;
      }
      const pct = Math.round(processed / Math.max(questions.length, 1) * 100);
      setProgress(pct);
      setCurrentStage(stages[Math.floor(pct / 100 * stages.length)] ?? 'Afronden...');
      if (processed < questions.length) {
        setTimeout(processBatch, 0);
      } else {
        setRunStatus('complete');
        setResults(allResults);
        setRunDuration(`${((Date.now() - startTime) / 1000).toFixed(1)}s`);
        setCurrentStage('');
      }
    };
    // Start processing after a tick so the UI can update
    setTimeout(processBatch, 50);
  };
  const filteredResults = results.filter(r => {
    const matchesFilter = filter === 'all' || r.status === filter;
    const matchesSearch = search === '' || r.questionId.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });
  const blockedCount = results.filter(r => r.status === 'blocked').length;
  const warningCount = results.filter(r => r.status === 'warning').length;
  const passCount = results.filter(r => r.status === 'pass').length;
  const avgScore = results.length > 0 ? Math.round(results.reduce((sum, r) => sum + r.score, 0) / results.length) : 0;
  const scopes = [{
    value: 'all',
    label: 'Alle vragen'
  }, {
    value: 'GET',
    label: 'Getallen (GET)'
  }, {
    value: 'MET',
    label: 'Meten (MET)'
  }, {
    value: 'SPR',
    label: 'Spelling (SPR)'
  }, {
    value: 'TAA',
    label: 'Taal (TAA)'
  }, {
    value: 'TGL',
    label: 'Tijd & Geld (TGL)'
  }, {
    value: 'TOPO',
    label: 'Topografie (TOPO)'
  }, {
    value: 'SWW',
    label: 'Werkwoorden (SWW)'
  }, {
    value: 'STU',
    label: 'Studievaardigheden (STU)'
  }];
  return <div className="space-y-5" data-id="element-3505">
      {/* Run Panel */}
      <div className="rounded-xl border border-gray-100 bg-white p-5" data-id="element-3506">
        <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-3507">
          Validatie Uitvoeren
        </h2>
        <p className="text-sm text-gray-500 mb-4" data-id="element-3508">
          Voer de volledige governance-pipeline uit op een selectie van vragen.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end" data-id="element-3509">
          <div className="flex-1" data-id="element-3510">
            <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-3511">
              Scope
            </label>
            <select value={selectedScope} onChange={e => setSelectedScope(e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" disabled={runStatus === 'running'} data-id="element-3512">
              {scopes.map(s => <option key={s.value} value={s.value} data-id="element-3513">
                  {s.label}
                </option>)}
            </select>
          </div>
          <Button variant="primary" onClick={handleRun} disabled={runStatus === 'running'} data-id="element-3514">
            {runStatus === 'running' ? <span className="flex items-center gap-2" data-id="element-3515">
                <RefreshCwIcon className="h-4 w-4 animate-spin" data-id="element-3516" />
                Valideren...
              </span> : <span className="flex items-center gap-2" data-id="element-3517">
                <PlayIcon className="h-4 w-4" data-id="element-3518" />
                Validatie Starten
              </span>}
          </Button>
        </div>

        {/* Progress Bar */}
        {runStatus === 'running' && <div className="mt-4" data-id="element-3519">
            <div className="flex items-center justify-between mb-1" data-id="element-3520">
              <span className="text-xs text-gray-500" data-id="element-3521">{currentStage}</span>
              <span className="text-xs font-medium text-blue-600" data-id="element-3522">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden" data-id="element-3523">
              <div className="h-full rounded-full bg-blue-500 transition-all duration-300" style={{
            width: `${progress}%`
          }} data-id="element-3524" />
            </div>
          </div>}
      </div>

      {/* Results */}
      {runStatus === 'complete' && results.length > 0 && <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-3525">
            <button onClick={() => setFilter('all')} className={`rounded-xl border p-3 text-center transition-all ${filter === 'all' ? 'border-gray-300 bg-gray-50 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-3526">
              <p className="text-2xl font-bold text-gray-800" data-id="element-3527">
                {results.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-3528">Totaal</p>
            </button>
            <button onClick={() => setFilter('pass')} className={`rounded-xl border p-3 text-center transition-all ${filter === 'pass' ? 'border-emerald-300 bg-emerald-50 shadow-sm' : 'border-gray-100 bg-white hover:border-emerald-200'}`} data-id="element-3529">
              <p className="text-2xl font-bold text-emerald-700" data-id="element-3530">{passCount}</p>
              <p className="text-xs text-emerald-600" data-id="element-3531">Geslaagd</p>
            </button>
            <button onClick={() => setFilter('warning')} className={`rounded-xl border p-3 text-center transition-all ${filter === 'warning' ? 'border-amber-300 bg-amber-50 shadow-sm' : 'border-gray-100 bg-white hover:border-amber-200'}`} data-id="element-3532">
              <p className="text-2xl font-bold text-amber-700" data-id="element-3533">
                {warningCount}
              </p>
              <p className="text-xs text-amber-600" data-id="element-3534">Waarschuwingen</p>
            </button>
            <button onClick={() => setFilter('blocked')} className={`rounded-xl border p-3 text-center transition-all ${filter === 'blocked' ? 'border-red-300 bg-red-50 shadow-sm' : 'border-gray-100 bg-white hover:border-red-200'}`} data-id="element-3535">
              <p className="text-2xl font-bold text-red-700" data-id="element-3536">{blockedCount}</p>
              <p className="text-xs text-red-600" data-id="element-3537">Geblokkeerd</p>
            </button>
          </div>

          {/* Meta info */}
          <div className="flex items-center justify-between" data-id="element-3538">
            <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-3539">
              <span className="flex items-center gap-1" data-id="element-3540">
                <ClockIcon className="h-4 w-4" data-id="element-3541" />
                Duur: {runDuration}
              </span>
              <span className="flex items-center gap-1" data-id="element-3542">
                <ZapIcon className="h-4 w-4" data-id="element-3543" />
                Gem. score:{' '}
                <strong className="text-gray-700 ml-1" data-id="element-3544">{avgScore}%</strong>
              </span>
            </div>
            <Button variant="secondary" size="sm" data-id="element-3545">
              <DownloadIcon className="h-4 w-4 mr-1.5" data-id="element-3546" />
              Exporteer Rapport
            </Button>
          </div>

          {/* Search */}
          <div className="relative" data-id="element-3547">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-3548" />
            <input type="text" placeholder="Zoek op vraag-ID..." value={search} onChange={e => setSearch(e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-4 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" data-id="element-3549" />
          </div>

          {/* Results List */}
          <div className="space-y-2" data-id="element-3550">
            {filteredResults.length === 0 ? <div className="rounded-lg border border-gray-100 bg-gray-50 py-10 text-center" data-id="element-3551">
                <p className="text-gray-500 text-sm" data-id="element-3552">
                  Geen resultaten voor deze filter.
                </p>
              </div> : filteredResults.map(result => <ResultRow key={result.questionId} result={result} data-id="element-3553" />)}
          </div>
        </>}

      {/* Idle State */}
      {runStatus === 'idle' && <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 py-16 text-center" data-id="element-3554">
          <ZapIcon className="mx-auto h-10 w-10 text-gray-300 mb-3" data-id="element-3555" />
          <p className="text-gray-500 font-medium" data-id="element-3556">
            Nog geen validatie uitgevoerd
          </p>
          <p className="text-sm text-gray-400 mt-1" data-id="element-3557">
            Klik op "Validatie Starten" om de pipeline uit te voeren.
          </p>
        </div>}
    </div>;
}